<script lang="ts">
  import { Trash2, Plus } from 'lucide-svelte';
  import Button from '../ui/button.svelte';
  import { targetLanguages, selectedTargetLanguage, flashcardActions } from '../../stores/flashcards';
  import type { FlashcardTargetLanguage } from '../../types';
  import { derived } from 'svelte/store';
  import { supabase } from '../../lib/supabase';
  import { _ } from 'svelte-i18n';
  import { getFlagUrl } from '../../lib/languageFlags';

  let { isOpen, onClose }: { isOpen: boolean; onClose: () => void } = $props();

  let showAddCustomLanguageForm = $state(false);
  let customLanguageName = $state('');
  let customLanguageCode = $state('');
  let customLanguageIsRtl = $state(false);
  let isAddingCustomLanguage = $state(false);

  // Removed: Now using centralized getFlagUrl from lib/languageFlags

  type LanguageSections = {
    top: FlashcardTargetLanguage[];
    other: FlashcardTargetLanguage[];
  };

  const TOP_LANGUAGE_CODES = ['en', 'es', 'fr', 'de', 'it', 'pt', 'ru', 'zh', 'ja', 'ko'];
  const normalizeLanguageCode = (code: string) => code?.toLowerCase().split('-')[0] ?? '';

  const languageSections = derived<[typeof targetLanguages], LanguageSections>(
    [targetLanguages],
    ([$tl]) => {
      const top: FlashcardTargetLanguage[] = [];
      const other: FlashcardTargetLanguage[] = [];

      $tl.forEach((lang) => {
        const normalizedCode = normalizeLanguageCode(lang.code);
        if (TOP_LANGUAGE_CODES.includes(normalizedCode)) {
          top.push(lang);
        } else {
          other.push(lang);
        }
      });

      top.sort((a, b) => {
        const aIndex = TOP_LANGUAGE_CODES.indexOf(normalizeLanguageCode(a.code));
        const bIndex = TOP_LANGUAGE_CODES.indexOf(normalizeLanguageCode(b.code));
        return aIndex - bIndex;
      });

      return { top, other };
    }
  );

  async function handleLanguageSelect(language: FlashcardTargetLanguage) {
    flashcardActions.selectTargetLanguage(language);
    onClose();
  }

  async function handleDeleteLanguage(languageId: string, e: Event) {
    e.stopPropagation();
    if (confirm($_('language.dialog.delete_confirm'))) {
      await flashcardActions.deleteCustomLanguage(languageId);
    }
  }

  async function handleAddCustomLanguage() {
    if (!customLanguageName.trim() || !customLanguageCode.trim()) {
      alert($_('dialogs.add_language.errors.missing_fields'));
      return;
    }

    isAddingCustomLanguage = true;
    try {
      const { data, error } = await supabase
        .from('flashcard_target_languages')
        .insert({
          name: customLanguageName,
          code: customLanguageCode.toLowerCase(),
          is_custom: true,
          is_rtl: customLanguageIsRtl,
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const newLanguage = data as FlashcardTargetLanguage;
        customLanguageName = '';
        customLanguageCode = '';
        customLanguageIsRtl = false;
        showAddCustomLanguageForm = false;
        // Optionally select the new language
        flashcardActions.selectTargetLanguage(newLanguage);
      }
    } catch (error) {
      console.error('Failed to add custom language:', error);
      alert($_('language.dialog.add_failed'));
    } finally {
      isAddingCustomLanguage = false;
    }
  }

    function handleClose() {
    onClose?.();
  }

  
  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      handleClose();
    }
  }
</script>

{#if isOpen}
  <div class="fixed inset-0 z-[96] flex flex-col bg-background"   
      role="dialog"
      aria-modal="true"
      tabindex="0"
      onkeydown={handleKeydown}
      >
    <div class="flex items-center justify-between px-6 pt-6">
      <div>
        <p class="section-label text-xs tracking-[0.28em] text-muted-foreground">{$_('language.dialog.subtitle')}</p>
        <h2 class="mt-1 text-2xl font-semibold text-foreground">{$_('language.dialog.title')}</h2>
      </div>
      <button
        class="btn btn-close"
        onclick={onClose}
        aria-label={$_('aria.close_target_language')}
      >
        <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <div class="flex-1 overflow-y-auto px-6 pb-24">
      {#if $languageSections.top.length > 0}
        <div class="space-y-4 pt-6">
          <p class="text-sm font-semibold uppercase tracking-wide text-muted-foreground break-words">{$_('language.dialog.most_used')}</p>
          <div class="grid gap-3">
            {#each $languageSections.top as language (language.id)}
              <button
                class={`flex items-center justify-between rounded-2xl border border-border bg-muted/50 px-4 py-4 text-left transition hover:border-primary/40 hover:bg-primary/10 text-foreground ${
                  $selectedTargetLanguage?.id === language.id
                    ? 'border-primary/60 bg-primary/20 shadow-soft-sm'
                    : ''
                }`}
                onclick={() => handleLanguageSelect(language)}
              >
                <div class="flex items-center gap-3">
                  <div class="h-8 w-12 rounded-lg overflow-hidden shadow-soft-sm flex-shrink-0">
                    <img
                      src={getFlagUrl(language.code)}
                      alt={language.name}
                      class="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <p class="text-base font-semibold text-foreground">{language.name}</p>
                  </div>
                </div>
                {#if $selectedTargetLanguage?.id === language.id}
                  <span class="rounded-full bg-primary/80 px-3 py-1 text-xs font-semibold text-primary-foreground">{$_('language.dialog.selected')}</span>
                {/if}
              </button>
            {/each}
          </div>
        </div>
      {/if}

      {#if $languageSections.other.length > 0 || true}
        <div class="mt-10 space-y-4">
          <p class="text-sm font-semibold uppercase tracking-wide text-muted-foreground break-words">{$_('language.dialog.custom_languages')}</p>
          {#if showAddCustomLanguageForm}
            <!-- Add Custom Language Form -->
            <div class="space-y-3 rounded-2xl border border-border bg-muted/30 p-4">
              <div>
                <label for="custom-lang-name" class="block text-sm font-medium text-foreground mb-2">
                  {$_('language.dialog.language_name')}
                </label>
                <input
                  id="custom-lang-name"
                  type="text"
                  bind:value={customLanguageName}
                  placeholder={$_('language.dialog.language_name_placeholder')}
                  class="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                />
              </div>

              <div>
                <label for="custom-lang-code" class="block text-sm font-medium text-foreground mb-2">
                  {$_('language.dialog.language_code')}
                </label>
                <input
                  id="custom-lang-code"
                  type="text"
                  bind:value={customLanguageCode}
                  placeholder={$_('language.dialog.language_code_placeholder')}
                  class="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  maxlength="2"
                />
                <p class="text-xs text-muted-foreground mt-1">
                  {$_('language.dialog.language_code_hint')}
                </p>
              </div>

              <div class="flex items-center gap-3">
                <input
                  id="custom-lang-rtl"
                  type="checkbox"
                  bind:checked={customLanguageIsRtl}
                  class="form-checkbox"
                />
                <label for="custom-lang-rtl" class="text-sm font-medium text-foreground cursor-pointer">
                  Right-to-Left (RTL) jazyk
                </label>
              </div>

              <div class="flex gap-2">
                <Button
                  onclick={() => {
                    showAddCustomLanguageForm = false;
                    customLanguageName = '';
                    customLanguageCode = '';
                    customLanguageIsRtl = false;
                  }}
                  variant="outline"
                  class="flex-1"
                  disabled={isAddingCustomLanguage}
                >
                  {$_('language.dialog.cancel')}
                </Button>
                <Button
                  onclick={handleAddCustomLanguage}
                  class="flex-1"
                  disabled={!customLanguageName.trim() || !customLanguageCode.trim() || isAddingCustomLanguage}
                >
                  {isAddingCustomLanguage ? $_('language.dialog.adding') : $_('language.dialog.add')}
                </Button>
              </div>
            </div>
          {:else}
            {#if $languageSections.other.length > 0}
              <div class="grid gap-3 pb-10">
                {#each $languageSections.other as language (language.id)}
                  <div
                    class={`flex items-center justify-between rounded-2xl border border-border bg-muted/30 px-4 py-4 text-left transition hover:border-primary/40 hover:bg-primary/10 text-foreground ${
                      $selectedTargetLanguage?.id === language.id
                        ? 'border-primary/60 bg-primary/20 shadow-soft-sm'
                        : ''
                    }`}
                  >
                    <button
                      class="flex items-center gap-3 flex-1 text-left text-foreground"
                      onclick={() => handleLanguageSelect(language)}
                    >
                      <div class="h-8 w-12 rounded-lg overflow-hidden shadow-soft-sm flex-shrink-0">
                        <img
                          src={getFlagUrl(language.code)}
                          alt={language.name}
                          class="h-full w-full object-cover"
                          loading="lazy"
                        />
                      </div>
                      <div>
                        <p class="text-base font-semibold text-foreground">{language.name}</p>
                      </div>
                    </button>
                    <div class="flex items-center gap-2">
                      {#if $selectedTargetLanguage?.id === language.id}
                        <span class="rounded-full bg-primary/80 px-3 py-1 text-xs font-semibold text-primary-foreground">{$_('language.dialog.selected')}</span>
                      {/if}
                      {#if language.is_custom}
                        <button
                          class="rounded-lg p-2 text-muted-foreground transition hover:bg-destructive/20 hover:text-destructive"
                          title={$_('language.dialog.delete_title')}
                          onclick={(e) => handleDeleteLanguage(language.id, e)}
                        >
                          <Trash2 size={18} />
                        </button>
                      {/if}
                    </div>
                  </div>
                {/each}
              </div>
            {/if}
            <Button
              onclick={() => (showAddCustomLanguageForm = true)}
              variant="outline"
              class="w-full"
            >
              <Plus class="w-4 h-4 mr-2" />
              {$_('language.dialog.add_custom')}
            </Button>
          {/if}
        </div>
      {/if}

    </div>
  </div>
{/if}

